/**
 * Which modules may open each Quoting, Billing and Support page (#3989, Nick ruling R2 2026-10-01: guard
 * each PAGE by the module its server actions need). ONE list per page: the route guard, the
 * route's `handle.anyOf`, the nav item and the landing-page option all read it from here, so
 * they cannot drift apart. This is a usability gate, NOT access control: the server checks every
 * action, and several read paths stay open to every employee in firestore.rules.
 *
 * External accounts (users.isExternal, outside contractors) are refused unless a page sets
 * `allowExternal`, mirroring the server, where a callable refuses External callers unless it
 * opts in. No Quoting or Billing page opts in (#5206 F1 kept contractors off InvoiceMatching);
 * SUPPORT_PAGE does, only so the Support menu hides pages from people without P5 and never
 * takes a link away from an External account that holds it (#4471).
 */
import type { ModuleId } from './types';

export interface PageAccess {
  readonly anyOf: readonly ModuleId[];
  readonly allowExternal?: true;
}

const page = (...anyOf: ModuleId[]): PageAccess => Object.freeze({ anyOf: Object.freeze(anyOf) });

/** Finance pages: P4, the same predicate as firestore.rules hasFinanceModule(). */
export const FINANCE_PAGE = page('P4');

/**
 * Support pages that need the Support module (P5). /support/signature and the on-call rota are open to every employee, so a
 * viewer without P5 still lands in this module; its nav lists only the pages they can open (#4471). `allowExternal` keeps
 * an outside contractor who holds P5 where they were before: this gate only hides pages from people without the module.
 */
export const SUPPORT_PAGE: PageAccess = Object.freeze({ anyOf: Object.freeze(['P5'] as ModuleId[]), allowExternal: true as const });

export const QUOTING_PAGES = Object.freeze({
  quoting: page('P2'),
  // Nick 2026-10-02: quoters keep Customers (12 of 13 active non-owner P2 users have no P6).
  customers: page('P2', 'P6'),
  customerDetail: page('P2', 'P6'),
  // Nick 2026-10-03 (MANUAL): quoting-only users KEEP the Equipment page, so P2 is listed here.
  equipment: page('P2', 'P6', 'P10', 'P11'),
});

/**
 * Creating, editing or deleting a unit: the generator callables are gated to P10/P11 on the
 * server, so a quoting-only (P2) viewer who may SEE Equipment is not offered these buttons
 * (they would end in a permission-denied toast). Owner always passes (see canOpenPage). A
 * usability gate, not access control: the server still checks every action.
 */
export const EQUIPMENT_EDIT_PAGE = page('P10', 'P11');

/**
 * Nick ruling 2026-10-02: creating or editing a bill is Billing (P4) only, so all three
 * invoicing work pages are finance-only. Projects and Field Service see bills through the
 * READ-ONLY views below, inside their own modules.
 */
export const BILLING_PAGES = Object.freeze({
  finance: FINANCE_PAGE,
  serviceInvoicing: FINANCE_PAGE,
  projectInvoicing: FINANCE_PAGE,
});

/** Read-only bill views, one per module, gated by that module alone. */
export const BILL_VIEW_PAGES = Object.freeze({
  projects: page('P10'),
  fieldService: page('P11'),
});

export interface PageViewer {
  readonly isOwner: boolean;
  readonly isExternal: boolean;
  readonly hasModule: (id: ModuleId) => boolean;
}

/** True when this viewer may open a page with this access. */
export function canOpenPage(access: PageAccess, viewer: PageViewer): boolean {
  if (viewer.isExternal && !access.allowExternal) return false;
  return viewer.isOwner || access.anyOf.some((id) => viewer.hasModule(id));
}
